import styled, { createGlobalStyle, css, DefaultTheme } from 'styled-components';

import media from '../../../utils/styling/media';
import CloseButton from '../../buttons/_CloseButton';

import { MARGIN_FROM_EDGE } from './Modal.constants';

function getScrollStyles({ theme, hasHeader, maxWidth, scrollInsideModal }: { theme: DefaultTheme; hasHeader: boolean; maxWidth: number; scrollInsideModal: boolean }) {
  const fullScroll = css`
    display: block;
    max-width: ${maxWidth}px;
    height: auto;
    max-height: calc(100% - ${MARGIN_FROM_EDGE * 2}px);
  
    ${hasHeader && css`
      &:after {
        content: '';
        display: block;
        padding-bottom: 24px;
      }
    `}
  `;

  const insideScroll = css`
    max-width: calc(100% - ${(theme?.Modal?.horizontalMarginFromEdgeXs || 0) * 2}px);
    height: 100%;
    max-height: 100%;
    display: flex;
    flex-direction: column;
  `;

  if (scrollInsideModal) {
    return insideScroll;
  }

  if (theme?.Modal.mobileFullScroll) {
    return fullScroll;
  }

  return css`
    ${media.xs`
      ${insideScroll}
    `}

    ${media.sm`
      ${fullScroll}
    `}
  `;
}

export const ModalGlobalStyles = createGlobalStyle<{
  scrollOffset: number;
  isClosable: boolean;
  hasHeader: boolean;
  maxWidth: number;
  scrollInsideModal: boolean;
}>`
  .ReactModal__Body--open {
    overflow: hidden;
    position: relative;
    margin-right: ${({ scrollOffset }) => scrollOffset ? `${scrollOffset}px` : ''};
  }

  .iA_Core_Modal-Overlay, .iA_Core_Modal-Content {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }
  
  .iA_Core_Modal-Overlay {
    background: ${({ theme }) => theme?.Modal?.overlayBackground};
    display: flex;
    flex-direction: column;
    z-index: 100000;
    overflow-x: hidden;

    ${media.xs`
      overflow-y: ${({ theme }) => theme?.Modal.mobileFullScroll ? 'auto' : 'visible'};
    `}

    ${media.sm`
      overflow-y: auto;
    `}
  }

  .iA_Core_Modal {
    font-family: ${({ theme }) => theme.fontFamily.regular};
    width: 100%;
    margin: auto;
    border-radius: 0;
    outline: 0;

    @media (max-width: ${({ maxWidth }) => maxWidth + (MARGIN_FROM_EDGE * 2)}px) {
      max-width: calc(100% - ${MARGIN_FROM_EDGE * 2}px);
    }

    ${getScrollStyles}
  }
`;

export const StyledModalHeader = styled.header<{ headerBackground: string }>`
  background: ${({ theme, headerBackground }) => headerBackground || theme?.Modal?.headerBackground || theme.colors.primary};
  font-size: 18px;
  line-height: 1;
  color: ${({ theme }) => theme.colors.white};
  height: ${({ theme }) => theme?.Modal?.headerHeight}px;
  flex-shrink: 0;
  text-transform: uppercase;
`;

export const StyledHeaderIcon = styled.svg`
  height: 24px;
  width: auto;
`;

export const StyledModalContent = styled.section<{
  align: string;
  nopadding: boolean;
  hasFooter: boolean;
  isMobile: boolean;
  hasContentHeader: boolean;
  scrollInsideModal: boolean;
}>`
  min-height: 20px;
  background: ${({ theme }) => theme?.Modal?.background || theme.colors.smoothGray};
  box-sizing: border-box;
  flex-grow: 1;
  flex-shrink: 0;
  text-align: ${({ align }) => align};
  padding: ${({ theme, nopadding, hasFooter, isMobile, hasContentHeader }) => {
    const { verticalPadding, horizontalPadding, horizontalPaddingXs } = theme.Modal;

    const actualHorizontalPadding = isMobile ? horizontalPaddingXs : horizontalPadding;

    const topPadding = hasContentHeader ? 0 : verticalPadding;

    if (nopadding) {
      return '';
    }

    if (hasFooter) {
      return `${topPadding}px ${actualHorizontalPadding}px 0 ${actualHorizontalPadding}px`;
    }

    return `${topPadding}px ${actualHorizontalPadding}px ${verticalPadding}px ${actualHorizontalPadding}px`;
  }};
`;

function getScrollContentStyles({ theme, scrollInsideModal }: { theme: DefaultTheme; scrollInsideModal: boolean }) {
  const fullScroll = css`
    overflow: visible;
    max-height: none;
  `;

  const insideScroll = css`
    overflow: auto;
    max-height: calc(100% - ${theme.Modal.headerHeight + theme.Modal.bottomMarginFromEdgeXs}px);
    height: 0;
    margin-bottom: ${theme.Modal.bottomMarginFromEdgeXs}px;
  `;

  if (scrollInsideModal) {
    return insideScroll;
  }

  if (theme?.Modal.mobileFullScroll) {
    return fullScroll;
  }

  return css`
    ${media.xs`
      ${insideScroll}
    `}

    ${media.sm`
      ${fullScroll}
    `}
  `;
}

export const StyledModalContentWrapper = styled.div<{ scrollInsideModal: boolean }>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-basis: auto;
  flex-shrink: 0;

  ${getScrollContentStyles}
`;

export const StyledCloseButton = styled(CloseButton)`
  position: relative;
  right:  ${({ theme }) => theme?.Modal?.closeButtonRight}px;
  width: ${({ theme }) => theme?.Modal?.headerHeight}px;
  height: ${({ theme }) => theme?.Modal?.headerHeight}px;
`;

export const StyledContentHeader = styled.header<{ nopadding: boolean; isMobile: boolean }>`
  background: ${({ theme }) => theme?.Modal?.background || theme.colors.smoothGray};
  padding: ${({ theme, nopadding, isMobile }) => {
    const { verticalPadding, horizontalPadding, horizontalPaddingXs } = theme.Modal;

    if (nopadding) {
      return '';
    }

    const actualHorizontalPadding = isMobile ? horizontalPaddingXs : horizontalPadding;

    return `${verticalPadding}px ${actualHorizontalPadding}px 0 ${actualHorizontalPadding}px`;
  }};
  flex-shrink: 0;
`;

export const StyledFooter = styled.footer<{ nopadding: boolean; isMobile: boolean }>`
  padding: ${({ theme, nopadding, isMobile }) => {
    const { footerTopPadding, verticalPadding, horizontalPadding, horizontalPaddingXs } = theme.Modal;

    if (nopadding) {
      return '';
    }

    const actualHorizontalPadding = isMobile ? horizontalPaddingXs : horizontalPadding;
    const topPadding = footerTopPadding || `${actualHorizontalPadding}px`;

    return `${topPadding} ${actualHorizontalPadding}px ${verticalPadding}px ${actualHorizontalPadding}px`;
  }};
  background: ${({ theme }) => theme?.Modal?.background || theme.colors.smoothGray};
  flex-shrink: 0;
`;
